// janus-engine/reactions/trigger-dispatch.ts — lane RX S1 (2026-09-28): the janus_trigger() DISPATCH DECISION in the browser.
//
// A faithful transliteration of includes/functions/janus/core/janus_trigger.php + janus_trigger_helpers.php, split at the one seam PHP
// already has: this module decides WHETHER a carrier fires and WHICH wins the reaction slot; the fired ability's EFFECT is the host's
// (Lane A's ability pipeline in trigger mode, or a lane-local effect like the OA resolver's). Three phases, exactly as PHP:
//   PASS 1  collectTriggerCandidates()  — pure structural match over the nine carriers (janus_trigger_carriers() ORDER, load-bearing:
//           passive first, opportunity_attack_ability LAST so the universal OA loses PL ties), the `[x]` user-input skip, the id
//           allowlist, the per-entry predicate; then janus_trigger_rank_candidates_by_pl() on the reaction contenders only. The
//           STATUS pool (creature_payload_statuses + carrier authority) is collected and ranked separately and always runs after.
//   PASS 2  gateTriggerCandidate()      — the stateful gates in PHP's order (carrier-specific → reaction budget → mmdb.triggers opt-out →
//           reach → in-combat → team type → weapon), then the dispatch spec (targets / AoE auto-placement / variable_1).
//   LOOP    runTriggerDispatch()        — gate → execute → next gate, so a dispatched reaction's spend is what the next contender's
//           gate reads (the "strongest reaction wins, the rest silent-skip" rule). The executor is the host's.
// The decision point is kept separable from execution on purpose: the interrupt-window concept
// (plans/janus-reaction-interrupt-window-concept-2026-09-15.md) pauses between gate and execute.
//
// UNKNOWN IS NEVER "NO": a carrier definition the page does not hold, a status definition, or a PL a ranking needs throws
// TriggerInputUnavailable — the caller reports a named unavailable action/effect. Silently skipping an unresolvable carrier is exactly the
// wrong-answer class the migration exists to avoid.
// Parity: tools/probes/rx_trigger_oracle_capture.php → tools/test/janus-rx-trigger-dispatch.test.mjs.

import { pendingStatusDefinitions } from '../ability/context.js';
import { manaReductionDelta, chargeReductionDelta } from '../resources/reduction-guard.js';
import { ABILITY_CHARGE_BUCKETS, abilityEntryId as chargeEntryId } from '../ability/charges/charge-entry.js';
import { heldLayerlessDefinition } from '../compile/held-definition.js';
import { rowLayerNames } from '../compile/row-layer-names.js';
import { distinctUuids, isUuid, layerListEntryUuid, UUID } from './layer-list-ids.js';
export { heldLayerlessDefinition };
export { distinctUuids, layerListEntryUuid };
export type Row = Record<string, unknown>;

/** A dependency the page does not hold; the caller reports its named reason. */
export class TriggerInputUnavailable extends Error {
  constructor(public readonly reason: string) { super(`trigger-input-unavailable: ${reason}`); this.name = 'TriggerInputUnavailable'; }
}
const unavailable = (reason: string): never => { throw new TriggerInputUnavailable(reason); };

/** janus_trigger_carriers.php — ORDER IS LOAD-BEARING (see the header). */
export const TRIGGER_CARRIERS = ['passive_ability', 'reaction_ability', 'free_ability', 'menta', 'curse', 'at_will_spell',
  'innate_spell_per_long_rest', 'spells_known', 'opportunity_attack_ability'] as const;
export type TriggerCarrier = typeof TRIGGER_CARRIERS[number];
export const FORTUNATE_ID = 'AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8';
/** VitalityStatus::getAll() item ids — a tier lives on computed.vitality.v_status, not in the status list. */
export const VITALITY_STATUS_IDS = new Set(['82788F70-EBEF-44C4-80E7-6622AD20DD60', 'FF30B76E-785D-2CAF-FC66-C131D0548808', '658483AA-34DE-74E5-C974-E3E202C31CC7',
  'C97076EF-FED9-19B4-D109-C22F9BC740E8', '44B8792E-632E-2651-C666-67DFD181E92A', '298BE1DD-2917-476E-D79C-92CF8C598926',
  'D5DE14C4-3194-87BC-D767-6D45BDD90552', '8AE129AF-52D8-CCE8-706A-85767DB52740', '9FEE2E8A-9CF7-D74D-5D16-B86C2603A1FA']);

/** The inciting event — janus_trigger()'s optional parameters. */
export interface TriggerEvent {
  trigger: string;
  /** Damage-lane preflight knows the impending kill has a separate status-stripping death tail. */
  killDeathTail?: boolean;
  rollType?: string | null;
  hit?: boolean | null;
  criticalHit?: boolean | null;
  itemType?: string | null;
  spellLevel?: number | null;
  statusItemId?: string | null;
  damageTypes?: string[] | null;
  restrictToAbilityRollTypes?: string[] | null;
  excludeAbilityRollTypes?: string[] | null;
  /** Last Stand only (death/self-zero.ts tryLastStand): a charged ward held ONLY as a status (Death Ward Effect, cast by another creature) is
   *  its own single charge — the caller removes the status (ft_consume_self_zero_vitality_heal_ward_charges), so it needs no bucket pool. */
  statusIsCharge?: boolean;
  restrictToAbilitiesWithLayer?: string[] | null;
  excludeAbilitiesWithLayer?: string[] | null;
  restrictToAbilityIds?: string[] | null;
  excludeAbilityIds?: string[] | null;
  /** triggers_when_enemy_within_ft_reduced_to_zero_vitality only: janus_trigger()'s $zero_vitality_victim_arr — the creature that just
   *  dropped (the frame's inciting target is the witness itself). */
  zeroVitalityVictim?: Row | null;
  /** Wizard v9 Mastery port (2026-10-09): the ORIGINAL cast's spell circles (spell-circle-scope.ts spellCircles — upper-case ids; [] = a
   *  non-spell use or a circle-less spell). Set only by a family that knows the cast (triggers_when_caster_uses_item_type); undefined = no
   *  original-cast context, and a carrier with a circle predicate is then an executor gap (`original-cast-circle-not-held`). BROWSER RULE:
   *  PHP janus_trigger() never read if_circle_of_magic_cast / _not_cast (it fired Echoform Stability / Protective Surge on every spell). */
  spellCircles?: string[] | null;
}

/** triggers_when_enemy_within_ft_reduced_to_zero_vitality (int feet; Warlock v13 Claim the Departing, 2026-10-07). */
export const ZERO_VITALITY_WITNESS = 'triggers_when_enemy_within_ft_reduced_to_zero_vitality';
/** ft_zero_vitality_witness_allows(): the dropped creature is an ENEMY of this holder, within THIS carrier's own radius (holder rim to
 *  victim footprint — calculate_range_from_caster_to_target(..., target_rim: true)); an unmeasurable pair fails closed. */
export function zeroVitalityWitnessAllows(read: TriggerReaders, ability: Row, caster: Row, victim: Row | null): boolean {
  if (!victim) return false;
  const casterId = ecId(caster), victimId = ecId(victim);
  if (!casterId || !victimId || casterId === victimId) return false;
  if (!read.opposing(caster, victim)) return false;
  const radius = Math.trunc(Number(read.value(ability, ZERO_VITALITY_WITNESS) ?? 0));
  if (!Number.isFinite(radius) || radius <= 0) return false;
  const distance = read.distanceFt(caster, victim);
  return distance !== null && distance <= radius;
}
/** Could `row` answer a zero-Vitality drop at all, WITHOUT its certified reaction compile (2026-10-08)? Its own layers and its statuses
 *  (statuses ride every row, filtered or not) are read here; its ability carriers come from the compile-time fact finalize_creature_arr.php
 *  stamps beside the reach triggers — computed.creature.reach_watch.zero_vitality_witness_ft: the largest radius any janus_trigger carrier
 *  authors (0 = none can fire; -1 = an entry the compile could not resolve). A row compiled before the fact existed proves nothing.
 *  'none' = no carrier can ever fire for this row; 'carries' / 'unknown' = only its full compile can say. */
export function zeroVitalityWitnessFact(read: TriggerReaders, row: Row): 'none' | 'carries' | 'unknown' {
  const authors = (def: Row): boolean => !phpEmpty(read.value(def, ZERO_VITALITY_WITNESS)) || read.entries(def, ZERO_VITALITY_WITNESS).length > 0;
  try {
    if (authors(row)) return 'carries';
    for (const status of payloadStatuses(read, row)) {
      const def = record(status.status_arr);
      if (!def) return 'unknown';
      if (authors(def)) return 'carries';
    }
  } catch { return 'unknown'; }
  const watch = record(record(record(row.computed)?.creature)?.reach_watch);
  if (!watch || watch.schema !== 'ft.reach-watch.v1') return 'unknown';
  const ft = watch.zero_vitality_witness_ft;
  if (typeof ft !== 'number' || !Number.isInteger(ft) || ft < 0) return 'unknown';
  return ft > 0 ? 'carries' : 'none';
}

/** What the host supplies. Every reader answers from the rows the page holds; none may guess. */
export interface TriggerReaders {
  conditionsMet?: (ability: Row, caster: Row) => boolean;
  /** layer_entries() / layer_value() / item_field() twins (item-arr-readers). */
  entries(row: Row | null, key: string): unknown[];
  value(row: Row | null, key: string): unknown;
  itemField(row: Row | null, key: string): unknown;
  /** ft_resolve_layer_entry(): the full catalog definition (with item_layer_types) or null when the page does not hold it. A catalog
   *  SHELL is not a definition. `allowLayerlessStatus`: a status_effect may legitimately author no layers (Awake) — accept it. */
  definition(id: string, allowLayerlessStatus?: boolean | 'any'): Row | null;
  /** ft_creature_in_combat(caster). */
  inCombat(caster: Row): boolean;
  /** ft_ec_authoritative_scalar(caster, 'reaction' | 'mana') — the saved value the page holds (confirmed + own pending). */
  scalar(caster: Row, field: 'reaction' | 'mana'): number;
  /** ability_entry_current_charges(): the persisted pool the page holds for this entry ('NA' / null = unlimited). */
  currentCharges(entry: Row, abilityId: string, caster: Row): unknown;
  /** check_opposing_team(caster, target). */
  opposing(caster: Row, target: Row): boolean;
  /** calculate_range_from_caster_to_target() in feet; null = unmeasurable (PHP passes the reach gate then). */
  distanceFt(caster: Row, target: Row): number | null;
  /** is_target_in_range(caster, target, ability)['in_range'] present. */
  inRange(caster: Row, target: Row, ability: Row): boolean;
  /** creature_statuses(target, 'target') catalog ids (has_status_effect). */
  statusItemIds(target: Row): string[];
  /** Working status rows, including this frame's pending changes when the host owns them. */
  statuses?(target: Row): Row[];
  /** v_status_id_of(target). */
  vStatusId(target: Row): string;
  /** creature_natural_weapon_entries(caster).length. */
  naturalWeaponCount(caster: Row): number;
  /** The definition's PL as the FRONT-END compile produces it (held number, else priced by the browser PL engine); null = the page cannot
   *  price it (the ranking then hands back). Only asked when 2+ reaction contenders compete. */
  pl(definition: Row): number | null;
  /** pos_coord(row, axis): the row's saved coordinate, null when absent. */
  position(row: Row, axis: 'x_pos' | 'y_pos' | 'z_pos'): number | null;
  /** The encounter id janus_trigger_auto_place_aoe() stamps into the span. */
  encounterId: string;
  /** pick_oa_kill_replacement_target(); only asked for an OA carrier on triggers_on_kill. Absent → `roster` + pickOaKillReplacement. */
  oaKillReplacement?(caster: Row, excluded: Row | null, ability: Row): Row | null;
  /** The encounter's member rows (encounter_creatures_arr) — pickOaKillReplacement's candidate pool. */
  roster?(): Row[];
}

export interface TriggerCandidate {
  pool: 'carrier' | 'status';
  carrier: TriggerCarrier | null;
  /** The normalised carrier wrapper ({item_arr: def, …live state}) or, for the status pool, the status row. */
  entry: Row;
  ability: Row;
  abilityId: string;
  order: number;
  competes: boolean;
  statusId?: string;
}
export interface TriggerDispatchSpec {
  candidate: TriggerCandidate;
  /** 'frame' = the frame's own targets_arr; 'caster' = [caster] (passive self-target); otherwise encounter creature ids. */
  targets: Array<'frame' | 'caster' | string>;
  /** status_casting_targets_arr ('' = none). */
  aoe: string;
  variable1: 'at_will_spell' | 'innate_spell_per_long_rest' | null;
  /** trigger_inciting_target_arr for a self-dispatched passive. */
  incitingForPassive: string | null;
}
export type TriggerGate = 'conditions' | 'live-entry-gone' | 'passive-inactive' | 'passive-conditions' | 'charges' | 'reaction-can-fire' | 'spell-can-fire'
  | 'reaction-budget' | 'db-opt-out' | 'range' | 'in-combat' | 'team' | 'weapon' | 'oa-kill-no-replacement' | 'summon-sub-type' | 'self-source'
  | 'zero-vitality-witness' | 'already-dispatched';
export type TriggerDecision = { fire: true; spec: TriggerDispatchSpec } | { fire: false; gate: TriggerGate };

// ---- small helpers (PHP helper twins) ---------------------------------------------------------------------------------------------
const record = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
/** PHP empty(): null, '', '0', 0, false, [] are empty. */
function phpEmpty(v: unknown): boolean {
  return v === undefined || v === null || v === '' || v === '0' || v === 0 || v === false || (Array.isArray(v) && v.length === 0)
    || (record(v) !== null && Object.keys(v as Row).length === 0);
}
const ecId = (row: Row | null): string => upper(record(row?.encounter_creatures)?.encounter_creature_id);

/** layer_has(): layer_value non-null OR layer_entries non-empty. */
function layerHas(read: Pick<TriggerReaders, 'value' | 'entries'>, row: Row | null, key: string): boolean {
  if (!row) return false;
  const v = read.value(row, key);
  return (v !== null && v !== undefined) || read.entries(row, key).length > 0;
}
/** ability_entry_id(). */
export function abilityEntryId(read: TriggerReaders, entry: unknown): string {
  if (typeof entry === 'string') return entry;
  const e = record(entry);
  if (!e) return '';
  if (typeof e.ability_id === 'string' && e.ability_id !== '') return e.ability_id;
  if (typeof e.item_arr === 'string' && e.item_arr !== '') return e.item_arr;
  if (!('item_arr' in e) && typeof e.item_id === 'string' && e.item_id !== '') return e.item_id;
  // _ft_ability_entry_layer_src(): a layerless shell resolves through the catalog (_ft_resolve_carrier_ref) and the id is read off
  // the RESOLVED definition — a lowercase shell id becomes the catalog's canonical one (Codex S1 review F5).
  let src = record(e.item_arr) ?? e;
  const nested = record(e.item_arr);
  if (nested && phpEmpty(nested.item_layer_types)) {
    const shellId = record(nested.items)?.item_id;
    if (typeof shellId === 'string' && shellId !== '') src = read.definition(upper(shellId)) ?? nested;
  }
  const id = read.itemField(src, 'item_id') ?? layerListEntryUuid(src);
  return typeof id === 'string' ? id : '';
}
/** ft_entry_restore_item_arr() + the collect loop's flat-entry wrap. null = the entry is skipped (PHP `continue`). */
function normaliseEntry(read: TriggerReaders, raw: unknown): Row | null {
  let entry = record(raw);
  if (!entry) return null; // PHP: `if (!is_array($entry)) continue;` — a bare-UUID carrier ref never fires a trigger
  if (!('item_arr' in entry) && typeof entry.item_id === 'string' && isUuid(entry.item_id)) entry = { ...entry, item_arr: entry.item_id };
  if (!layerHas(read, entry, 'item_arr') && !phpEmpty(read.itemField(entry, 'item_id'))) entry = { item_arr: entry };
  if (!layerHas(read, entry, 'item_arr')) return null;
  return entry;
}
/** ft_layer_entry_needs_resolve() + ft_resolve_layer_entry(): the full definition or TriggerInputUnavailable. */
function resolveDefinition(read: TriggerReaders, slot: unknown, reason: string): Row {
  const full = record(slot);
  if (full && (!phpEmpty(full.item_layer_types) || heldLayerlessDefinition(full))) return full;
  const id = layerListEntryUuid(slot);
  if (!id) return unavailable(`${reason}:unreadable-ref`);
  const def = read.definition(upper(id), 'any');
  // A HELD layer-less definition (the object-API payload's own markers, no layers — e.g. the "Insectoid Avatar" tier passives) is a
  // real carrier with nothing to fire; only a bare identity shell {items:{…}} is unknown (lane RX #5, reactor oracle 2026-09-29).
  const layerless = !!def && phpEmpty(def.item_layer_types) && heldLayerlessDefinition(def);
  if (!def || (phpEmpty(def.item_layer_types) && !layerless)) return unavailable(`${reason}:definition-not-held:${upper(id)}`);
  return def;
}

/** janus_trigger_carrier_entries(): the bridged bucket ∪ the raw root slot (deduped by ability id); spells_known hydrated. */
export function carrierEntries(read: TriggerReaders, caster: Row, carrier: TriggerCarrier): unknown[] {
  if (carrier === 'spells_known') return read.entries(caster, 'spells_known').map(entry => hydrateSpellEntry(read, entry));
  const entries = [...read.entries(caster, carrier)];
  const root = caster[carrier];
  if (!phpEmpty(root) && Array.isArray(root)) {
    const seen = new Set<string>();
    for (const e of entries) { const id = record(e) ? abilityEntryId(read, e) : String(e ?? ''); if (id !== '') seen.add(id); }
    for (const e of root) {
      const id = record(e) ? abilityEntryId(read, e) : String(e ?? '');
      if (id !== '' && seen.has(id)) continue;
      if (id !== '') seen.add(id);
      entries.push(e);
    }
  }
  return entries;
}
/** ft_hydrate_spell_entry(): a leaned pointer becomes array_merge(full spell, provenance meta). */
function hydrateSpellEntry(read: TriggerReaders, entry: unknown): unknown {
  if (typeof entry === 'string') {
    if (entry === '' || !isUuid(entry)) return entry;
    return read.definition(upper(entry)) ?? unavailable(`spells_known:definition-not-held:${upper(entry)}`);
  }
  const e = record(entry);
  if (!e) return entry;
  if (e.item_layer_types !== undefined) return entry;
  const id = read.itemField(e, 'item_id') ?? e.item_id;
  if (!isUuid(id)) return entry;
  const full = read.definition(upper(id)) ?? unavailable(`spells_known:definition-not-held:${upper(id)}`);
  const meta = { ...e }; delete meta.items; delete meta.item_id;
  return { ...full, ...meta };
}

/** janus_trigger_ability_requires_user_input(): any `[x]` string anywhere under item_layer_types. */
export function requiresUserInput(ability: Row): boolean {
  const walk = (v: unknown): boolean => typeof v === 'string' ? v.toLowerCase().includes('[x]')
    : Array.isArray(v) ? v.some(walk) : record(v) ? Object.values(v as Row).some(walk) : false;
  return walk(ability.item_layer_types ?? []);
}
/** entry_consumes_reaction(). */
export function consumesReaction(read: TriggerReaders, ability: Row): boolean {
  const type = String(read.itemField(ability, 'item_type') ?? '');
  if (type === 'reaction_ability' || type === 'opportunity_attack_ability') return true;
  return read.value(ability, 'spell_casting_time') === 'reaction';
}
/** janus_trigger_carrier_has_trigger_layer(). */
export function carrierHasTriggerLayer(read: TriggerReaders, ability: Row, trigger: string): boolean {
  // The typed damage event owns these hooks, even on historical rows also listing a roll event.
  if (trigger === 'triggers_when_caster_makes_roll_type'
    && ['triggers_when_caster_takes_damage_type', 'triggers_when_target_takes_damage_type'].some(k => layerHas(read, ability, k))
    && ['triggers_on_critical_hit', 'triggers_on_not_a_critical_hit'].some(k => layerHas(read, ability, k))) return false;
  const implicit = trigger === 'triggers_when_caster_makes_roll_type'
    && !layerHas(read, ability, 'triggers_on_roll_type_against_the_caster')
    && !layerHas(read, ability, 'triggers_on_roll_type_against_the_target')
    && ['triggers_on_hit', 'triggers_on_miss', 'triggers_on_critical_hit', 'triggers_on_not_a_critical_hit'].some(k => !phpEmpty(read.value(ability, k)));
  const scalar = read.value(ability, trigger);
  const entries = read.entries(ability, trigger);
  if ((scalar === null || scalar === undefined || scalar === '') && entries.length === 0) return implicit;
  if (trigger === 'triggers_when_caster_makes_roll_type' || trigger === 'triggers_when_target_makes_roll_type') return distinctUuids(entries).length > 0;
  return true;
}
/** The carrier's circle predicate against the original cast's circles (event.spellCircles): `if_circle_of_magic_cast` — one listed circle
 *  was cast; `if_circle_of_magic_not_cast` — none of the listed circles was cast. No predicate authored = match. */
export function circlePredicateMatch(read: Pick<TriggerReaders, 'entries'>, circles: readonly string[], ability: Row): boolean {
  const cast = new Set(circles.map(c => c.toUpperCase()));
  const listed = (key: string): string[] => distinctUuids(read.entries(ability, key)).map(id => id.toUpperCase());
  const scope = listed('if_circle_of_magic_cast'), excluded = listed('if_circle_of_magic_not_cast');
  if (scope.length && !scope.some(id => cast.has(id))) return false;
  return !excluded.some(id => cast.has(id));
}
/** janus_trigger_entry_predicate_match(). `statusAndDamage` false = the status pool's 6-argument call (no status / damage types). */
export function predicateMatch(read: Pick<TriggerReaders, 'value' | 'entries'>, event: TriggerEvent, ability: Row, statusAndDamage = true): boolean {
  if (Number(read.value(ability, 'trigger_only_leveled_spells')) === 1 && (event.spellLevel ?? 0) < 1) return false;
  if (event.spellCircles != null && !circlePredicateMatch(read, event.spellCircles, ability)) return false;
  const t = event.trigger;
  const statusId = statusAndDamage ? event.statusItemId ?? null : null;
  const damageTypes = statusAndDamage ? event.damageTypes ?? null : null;
  if (['triggers_when_caster_gains_status_effect', 'triggers_when_target_gains_status_effect',
    'triggers_when_caster_loses_status_effect', 'triggers_when_target_loses_status_effect'].includes(t)) {
    const layered = distinctUuids(read.entries(ability, t));
    return layered.length > 0 && !!statusId && layered.includes(statusId);
  }
  if (t === 'triggers_when_caster_takes_damage_type' || t === 'triggers_when_target_takes_damage_type') {
    if (!phpEmpty(read.value(ability, 'triggers_on_critical_hit')) && event.criticalHit !== true) return false;
    if (!phpEmpty(read.value(ability, 'triggers_on_not_a_critical_hit')) && event.criticalHit !== false) return false;
    const layered = distinctUuids(read.entries(ability, t));
    return layered.length > 0 && Array.isArray(damageTypes) && damageTypes.length > 0 && layered.some(id => damageTypes.includes(id));
  }
  if (t === 'triggers_when_caster_uses_item_type' || t === 'triggers_when_caster_gets_item_type_against') {
    const layered = read.entries(ability, t);
    return layered.length > 0 && !!event.itemType && layered.some(v => v === event.itemType);
  }
  if (t === 'triggers_on_roll_type_against_the_caster' || t === 'triggers_on_roll_type_against_the_target') {
    const layered = distinctUuids(read.entries(ability, t));
    if (!layered.length || !event.rollType || !layered.includes(event.rollType)) return false;
    if (!phpEmpty(read.value(ability, 'triggers_on_hit')) && event.hit !== true) return false;
    return true;
  }
  if (t === 'triggers_when_caster_makes_roll_type') {
    const rt = event.rollType;
    if (!rt || !isUuid(rt)) return false;
    const layer = read.entries(ability, t);
    const onHit = !phpEmpty(read.value(ability, 'triggers_on_hit')), onMiss = !phpEmpty(read.value(ability, 'triggers_on_miss'));
    const onCrit = !phpEmpty(read.value(ability, 'triggers_on_critical_hit')), onNotCrit = !phpEmpty(read.value(ability, 'triggers_on_not_a_critical_hit'));
    const directional = layerHas(read, ability, 'triggers_on_roll_type_against_the_caster') || layerHas(read, ability, 'triggers_on_roll_type_against_the_target');
    if (layer.length > 0) { if (!distinctUuids(layer).includes(rt)) return false; }
    else if (!(onHit || onMiss || onCrit || onNotCrit) || directional) return false;
    if (onHit && event.hit !== true) return false;
    if (onMiss && event.hit !== false) return false;
    if (onCrit && event.criticalHit !== true) return false;
    if (onNotCrit && (event.hit !== true || event.criticalHit === true)) return false;
    return true;
  }
  return true;
}
/** The pass-1 partitioning (roll types, layer keys) shared by both pools. */
function partitionAllows(read: TriggerReaders, event: TriggerEvent, ability: Row): boolean {
  if (event.restrictToAbilityRollTypes || event.excludeAbilityRollTypes) {
    const rt = String(read.value(ability, 'roll_type') ?? '');
    if (event.restrictToAbilityRollTypes && !event.restrictToAbilityRollTypes.includes(rt)) return false;
    if (event.excludeAbilityRollTypes && event.excludeAbilityRollTypes.includes(rt)) return false;
  }
  if (event.restrictToAbilitiesWithLayer && !event.restrictToAbilitiesWithLayer.some(k => layerHas(read, ability, k))) return false;
  if (event.excludeAbilitiesWithLayer && event.excludeAbilitiesWithLayer.some(k => layerHas(read, ability, k))) return false;
  return true;
}

/** janus_trigger_inject_universal_fortunate(): a transient reaction entry for the two Luck events (idempotent). */
export function injectUniversalFortunate(read: TriggerReaders, caster: Row, trigger: string): Row {
  if (trigger !== 'triggers_when_caster_turn_begins' && trigger !== 'triggers_when_caster_gains_luck') return caster;
  const existing = [...read.entries(caster, 'reaction_ability'), ...(Array.isArray(caster.reaction_ability) ? caster.reaction_ability as unknown[] : [])];
  if (existing.some(e => record(e) && abilityEntryId(read, e) === FORTUNATE_ID)) return caster;
  const def = read.definition(FORTUNATE_ID);
  if (!def) return unavailable('fortunate-definition-not-held');
  return { ...caster, reaction_ability: [...(Array.isArray(caster.reaction_ability) ? caster.reaction_ability as unknown[] : []),
    { ability_id: FORTUNATE_ID, item_arr: def, source: 'host', ability_charges: 'NA', max_ability_charges: 'NA', requires_level: 0 }] };
}

export const OPPORTUNITY_ATTACK_ID = 'D1EADC34-832C-1909-2FC3-722AC857FBEA';
/** inject_universal_opportunity_attack_into_caster_arr(): the synthetic OA carrier a reach / stand-up dispatch walks (idempotent). */
export function injectUniversalOpportunityAttack(read: TriggerReaders, caster: Row): Row {
  for (const e of read.entries(caster, 'opportunity_attack_ability')) if (record(e) && abilityEntryId(read, e) === OPPORTUNITY_ATTACK_ID) return caster;
  const def = read.definition(OPPORTUNITY_ATTACK_ID);
  if (!def) return unavailable('opportunity-attack-definition-not-held');
  return { ...caster, opportunity_attack_ability: [...(Array.isArray(caster.opportunity_attack_ability) ? caster.opportunity_attack_ability as unknown[] : []),
    { ability_id: OPPORTUNITY_ATTACK_ID, item_arr: def, source: OPPORTUNITY_ATTACK_ID, ability_charges: 'NA', max_ability_charges: 'NA', requires_level: 0 }] };
}

/** A PHP list that JSON may carry as an OBJECT (a keyed array with gaps — ft_status_target_unset() keeps the keys; Codex S1 F4). */
function phpList(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  const rec = record(value);
  return rec ? Object.values(rec) : [];
}
/** ft_status_is_hostile_aura_self_anchor(): a self-anchored persistent aura that only affects the opposing team delivers no payload
 *  to its own holder. The browser row ELIDES a self-cast caster id (creature_statuses() re-stamps it as the target id). */
function hostileAuraSelfAnchor(read: TriggerReaders, status: Row, def: Row, bearer: Row): boolean {
  const norm = (v: unknown): string => { let x = v; if (Array.isArray(x)) x = x[0] ?? ''; return typeof x === 'string' ? x.trim() : ''; };
  let source: unknown = status.status_aoe_source ?? 0;
  if (Array.isArray(source)) source = source[0] ?? 0;
  if (Math.trunc(Number(source)) !== 1) return false;
  const target = norm(status.status_target_id) || norm(record(bearer.encounter_creatures)?.encounter_creature_id);
  const casterRaw = status.status_caster_id;
  const caster = casterRaw === undefined || casterRaw === null ? target : norm(casterRaw);
  if (target === '' || caster.toUpperCase() !== target.toUpperCase()) return false;
  for (const key of ['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target']) if (read.value(def, key) === null || read.value(def, key) === undefined) return false;
  return !phpEmpty(read.value(def, 'effect_only_affects_opposing_team_type')) && phpEmpty(read.value(def, 'effect_only_affects_same_team_type'));
}
/** creature_payload_statuses(caster) with each row's status_arr resolved (the status pool's input). */
export function heldStatusDefinition(read: Pick<TriggerReaders, 'definition' | 'itemField'>, status: Row, bearer: Row): Row | null {
  const stored = phpList(record(bearer.statuses)?.target).map(record)
    .find(row => row && upper(row.status_id) === upper(status.status_id));
  const embedded = record(stored?.status_arr);
  if (embedded) return embedded;
  const resolved = record(status.status_arr);
  const pending = pendingStatusDefinitions.get(status);
  if (pending) return pending;
  if (resolved && ('item_layer_types' in resolved || record(resolved.computed) || record(resolved._meta))) return resolved;
  const ref = status.status_item_id ?? (typeof stored?.status_arr === 'string' ? stored.status_arr : null)
    ?? (typeof status.status_arr === 'string' ? status.status_arr : read.itemField(resolved, 'item_id'));
  return typeof ref === 'string' && isUuid(ref) ? read.definition(upper(ref), true) : null;
}
function payloadStatuses(read: TriggerReaders, caster: Row): Row[] {
  const list = read.statuses ? read.statuses(caster) : phpList(record(caster.statuses)?.target);
  return list.map(record).filter((s): s is Row => !!s).map(s => {
    const stored = phpList(record(caster.statuses)?.target).map(record)
      .find(row => row && upper(row.status_id) === upper(s.status_id));
    const embedded = record(stored?.status_arr);
    // creature_statuses() preserves every nested array, even a layerless definition
    // or a deleted-template identity shell. Only a reference needs catalog resolution.
    if (embedded) return { ...s, status_arr: embedded };
    // ftCreatureStatuses() may synthesize an identity shell for a bare ref its
    // own catalog cannot resolve. That is not an embedded definition the page held.
    const resolved = record(s.status_arr);
    if (pendingStatusDefinitions.has(s) && resolved) return s;
    if (resolved && ('item_layer_types' in resolved || record(resolved.computed) || record(resolved._meta))) return s;
    const id = upper(s.status_item_id ?? (typeof stored?.status_arr === 'string' ? stored.status_arr : null)
      ?? (typeof s.status_arr === 'string' ? s.status_arr : read.itemField(resolved, 'item_id')));
    if (!isUuid(id)) return unavailable('status:unreadable-id');
    const def = heldStatusDefinition(read, s, caster);
    if (!def) return unavailable(`status:definition-not-held:${id}`);
    return { ...s, status_arr: def };
  }).filter(s => !hostileAuraSelfAnchor(read, s, record(s.status_arr)!, caster));
}
/** StatusEffect::resolveQuantityAlias(): quantity-group alias ids are stored as their base row (Melting → Burning …).
 *  Table copied from janus-engine/ability/statuses/apply-status-effect.ts (QUANTITY_ALIAS_BASE, not exported) — keep in step. */
const QUANTITY_ALIAS_BASE: Readonly<Record<string, string>> = Object.fromEntries(([
  ['09FC38BC-6060-4B67-A33F-EF7D448F71F1', ['996886D6-67F7-2A7D-0336-9324C877CAA6']],
  ['F632281D-2F16-FFDA-FB95-2E958988F01F', ['F90FB02F-2549-6FE0-A162-CC3FA7C90C67']],
  ['3BE713AB-37D7-630B-C1F8-9470784BA33E', ['22E33A8E-DF5D-2596-8E2D-0E8A8528E47C', 'BAAA237B-1AA7-0409-5BC7-1A6720573069']],
  ['A5CDC77C-D224-DD5F-291B-EF8DF2EA443D', ['9437F344-1461-B4C0-E196-6BA1A34ADC94', '9154FF1F-721A-2BCA-1AEF-24AC7B32FE4B',
    '46F27428-2B73-50F7-6F0C-D4FAFDB753ED', '4D27433B-6F72-47BB-9A60-B80493F22D65', 'E4994653-49B3-D768-35C1-36C8DE40CC1A',
    '5C557D5A-B85F-DF45-3CF6-6739D2BD70EC', '44D4504C-9BD5-0B65-F8E2-6DC4A0F820B4', '1DEE12B6-D23A-7470-812E-CE4C77AAD395',
    '4AEF8F40-B779-C0AD-4455-2DABE7B1D14B']],
  ['CDC5CFDA-B09C-4A68-820D-6C74D90BD595', ['D9B64F38-A817-47B8-AB07-60D59F3D893C', 'AE20C09B-76BE-5BC4-4E33-6E8CFE1F0139',
    '479A4B5C-43F3-3559-979B-5BDA8A379F07']],
  ['CEF623E6-6A71-37FD-29C7-12988B43151A', ['F8E5F274-2F31-1340-6167-ED9131E81A88', 'E18A79D1-AA69-7F80-2579-78B583E0771B',
    '20F461C2-67CC-BEF6-E02A-4E9A464EC397', 'EF165E68-DD93-90C8-A848-01D93B041CBD', 'DA5A18C5-FDE5-D20B-0E3B-07B68E6C7C1C',
    '683632D7-756C-0165-9E94-27EA95A53A53', 'E19F0AB8-E760-D84A-85C3-1A14E7DFE399', '5F87B80F-A8E4-A3BE-6FC6-3B5BC0A669E9',
    'E29189CC-A2FA-B06A-5DBD-8551A3259829', 'BDAF9378-AA7A-445F-D24F-8E6A0EE84456']],
  ['4744C91C-51DC-4C36-8C6E-DB801410FF61', ['5CB84B6E-E636-B4A3-3864-910DBD3B63B4', '1B912A78-2334-D265-2674-7AD764534D56',
    '7F938839-F44A-FA43-05FA-BAAF79B7A05B', 'FCC88F77-777D-56B1-913E-E676C90E39E3', '655383C0-8B0C-3B33-694D-26404400626D',
    '36B68995-C875-199D-20E8-1A1098F846B0', 'D9668790-E866-861A-FDE0-D23D2903B0FA', '90D143BD-9CC9-06F7-F339-03F50C27D958',
    '8A3B8D76-ED7A-560A-166D-73093DE6F4E9', 'E64821CE-A9A5-D2C3-4116-8A8D5DCA4D30']],
  ['EB6632D3-5EC0-4E4D-5C6C-1BCB7EF57022', ['BF823982-6FC8-51F0-F452-5BE1C949FBA6', '36DA5B89-CA08-3940-E798-FCAE6BE775D5',
    '8346CCFF-3A5B-F0E4-CFDE-6CF1D1ED3368', 'E5314BED-1269-3A65-8C15-01E80A3A8E23', 'E614D75F-CF27-D169-3D91-0D33D9CE354B',
    'A831D8E0-4DC5-4FBF-4A4C-2C9955AE230A', '631AAD91-F6A5-879D-5BD6-B7F885AA7FC9', 'BF1C1C48-8171-90B8-CD83-F2768348629F',
    '2A63EC91-E5B1-17B4-DFB0-199F9E2084A5', '01B0F693-7D8A-C588-9B34-CD1F323D8433']],
] as const).flatMap(([base, aliases]) => aliases.map((alias) => [alias, base])));
export const resolveQuantityAlias = (id: string): string => QUANTITY_ALIAS_BASE[id] ?? id;
/** janus_status_row_grants_trigger_carrier_authority(). The browser row ELIDES a self-cast caster id (creature_statuses re-stamps it). */
export function statusGrantsCarrierAuthority(read: TriggerReaders, status: Row, def: Row, bearer: Row): boolean {
  const norm = (v: unknown): string => { let x = v; if (Array.isArray(x)) x = x[0] ?? ''; return typeof x === 'string' ? x.trim() : ''; };
  let bearerId = norm(record(bearer.encounter_creatures)?.encounter_creature_id);
  const placerRaw = status.status_caster_id;
  const placer = norm(placerRaw);
  const absent = placerRaw === undefined || placerRaw === null || placerRaw === '' || (Array.isArray(placerRaw) && placerRaw.length === 0);
  if (bearerId === '') bearerId = norm(status.status_target_id);
  if (absent) return true;
  if (bearerId !== '' && placer.toUpperCase() === bearerId.toUpperCase()) return true;
  return read.itemField(def, 'item_type') === 'passive_ability' && phpEmpty(read.value(def, 'aoe_type'));
}

/** janus_trigger_rank_candidates_by_pl(): only reaction contenders move, PL desc, original order breaks ties. */
export function rankCandidates(read: TriggerReaders, candidates: TriggerCandidate[]): TriggerCandidate[] {
  if (candidates.length < 2) return candidates;
  const slots: number[] = [], competing: Array<TriggerCandidate & { pl: number }> = [];
  candidates.forEach((c, i) => { if (c.competes) { slots.push(i); competing.push({ ...c, pl: 0 }); } });
  if (competing.length < 2) return candidates;
  // STRONGEST FIRST by the FRONT-END compile's PL (owner 2026-09-28: the browser owns PL now, exactly as PHP's compile once stamped it on
  // every definition). A PL the page cannot produce hands back — never a silent 0 (PHP's own reader degrades to 0: BUG LEDGER 09-28 RX).
  for (const c of competing) {
    const pl = read.pl(c.ability);
    if (pl === null || !Number.isFinite(pl)) return unavailable(`rank:pl-not-held:${c.abilityId}`);
    c.pl = pl;
  }
  competing.sort((a, b) => (b.pl - a.pl) || (a.order - b.order));
  const out = [...candidates];
  slots.forEach((slot, i) => { const { pl: _pl, ...c } = competing[i]!; void _pl; out[slot] = c; });
  return out;
}

/** PASS 1 of the CARRIER pool (after the universal-Fortunate injection), ranked. */
export function collectCarrierCandidates(read: TriggerReaders, casterIn: Row, event: TriggerEvent): { caster: Row; carriers: TriggerCandidate[] } {
  const caster = event.excludeAbilityIds?.includes(FORTUNATE_ID) ? casterIn : injectUniversalFortunate(read, casterIn, event.trigger);
  const carriers: TriggerCandidate[] = [];
  let order = 0;
  for (const carrier of TRIGGER_CARRIERS) {
    for (const raw of carrierEntries(read, caster, carrier)) {
      const entry = normaliseEntry(read, raw);
      if (!entry) continue;
      const ability = resolveDefinition(read, entry.item_arr, `carrier:${carrier}`);
      const resolved = { ...entry, item_arr: ability };
      if (!partitionAllows(read, event, ability)) continue;
      if (!carrierHasTriggerLayer(read, ability, event.trigger)) continue;
      if (requiresUserInput(ability)) continue;
      const abilityId = abilityEntryId(read, resolved);
      if (abilityId === '' || !isUuid(abilityId)) continue;
      if (event.restrictToAbilityIds && !event.restrictToAbilityIds.includes(abilityId)) continue;
      if (event.excludeAbilityIds?.includes(abilityId)) continue;
      if (!predicateMatch(read, event, ability)) continue;
      carriers.push({ pool: 'carrier', carrier, entry: resolved, ability, abilityId, order: order++, competes: consumesReaction(read, ability) });
    }
  }
  return { caster, carriers: rankCandidates(read, carriers) };
}
/** Every ability id the bearer holds in a janus_trigger() carrier bucket — OWNERSHIP, read before any event filter or gate. spells_known
 *  is read raw (no hydration), so this never asks for a definition the carrier pass did not. */
function ownCarrierIds(read: TriggerReaders, caster: Row): Set<string> {
  const ids = new Set<string>();
  for (const carrier of TRIGGER_CARRIERS)
    for (const entry of carrier === 'spells_known' ? read.entries(caster, carrier) : carrierEntries(read, caster, carrier)) {
      const id = upper(abilityEntryId(read, entry));
      if (id !== '') ids.add(id);
    }
  return ids;
}
const MITIGATION_EFFECT_LAYERS = new Set(['damage_mitigation', 'range', 'cost_gp', 'unenchantable', 'override_ability',
  'if_armor_type_equipped_active', 'triggers_when_caster_takes_any_damage', 'effect_ends_on_start_of_caster_next_turn']);
/** An active ability's lingering effect never echoes its carrier's listener, including after a carrier veto.
 * A self-range reaction whose only effect is mitigation is already applied while held as a status, even without a carrier.
 * Its mitigation must still have compiled provenance (the damage resolver checks that). Payload-bearing status-only abilities
 * retain the executor's named gap; passive/status definitions keep their actual listeners. */
function lingeringActiveEffect(read: TriggerReaders, def: Row, id: string, owned: () => Set<string>): boolean {
  const type = String(read.itemField(def, 'item_type') ?? '');
  if (type === 'passive_ability' || type === 'status_effect') return false;
  if (owned().has(upper(id))) return true;
  return type === 'reaction_ability' && upper(read.value(def, 'range')) === 'D82855E1-4BB9-93EA-6374-B9904318BC1D'
    && layerHas(read, def, 'damage_mitigation')
    && rowLayerNames(def).every(key => MITIGATION_EFFECT_LAYERS.has(key) || /^requires_skill_tier_\d+$/.test(key));
}
/** PASS 1 of the STATUS pool, ranked. PHP collects it only AFTER every carrier dispatch ran, from the caster as those dispatches left
 *  it — so it must be called with the executor's returned row, never with a pre-dispatch snapshot (Codex S1 F2). */
export function collectStatusCandidates(read: TriggerReaders, caster: Row, event: TriggerEvent): TriggerCandidate[] {
  const statuses: TriggerCandidate[] = [];
  let sOrder = 0, owned: Set<string> | null = null;
  const ownedIds = (): Set<string> => owned ??= ownCarrierIds(read, caster);
  for (const status of payloadStatuses(read, caster)) {
    const def = record(status.status_arr)!;
    if (!carrierHasTriggerLayer(read, def, event.trigger)) continue;
    if (requiresUserInput(def)) continue;
    if (!statusGrantsCarrierAuthority(read, status, def, caster)) continue;
    if (!partitionAllows(read, event, def)) continue;
    if (!predicateMatch(read, event, def, false)) continue;
    const id = String(read.value(status, 'ability_id') ?? read.itemField(def, 'item_id') ?? '');
    if (id === '' || !isUuid(id)) continue;
    if (event.restrictToAbilityIds && !event.restrictToAbilityIds.includes(id)) continue;
    if (event.excludeAbilityIds?.includes(id)) continue;
    if (lingeringActiveEffect(read, def, id, ownedIds)) continue;
    statuses.push({ pool: 'status', carrier: null, entry: status, ability: def, abilityId: id, order: sOrder++, competes: consumesReaction(read, def), statusId: String(status.status_id ?? '') });
  }
  return rankCandidates(read, statuses);
}
/** A SUPERSET preview for a lane that runs no trigger fan-out of its own (A2's weapon attack): the first of `keys` any janus_trigger()
 *  carrier of `row` authors — its root layers, every carrier bucket (pass-1 normalisation), or a live status's definition — with no
 *  predicate / gate applied. null = none can ever fire; a definition the page does not hold throws TriggerInputUnavailable.
 *  `named` (optional) narrows a status-family key to the statuses the caller's lane can actually apply / remove: such a key authors only
 *  when its list NAMES one of those ids (janus_trigger_entry_predicate_match(): an unnamed status never fires; upper-cased, so a superset). */
export function authoredTriggerKey(read: TriggerReaders, row: Row, keys: readonly string[],
  named?: Readonly<Record<string, ReadonlySet<string>>>): string | null {
  const authors = (def: Row): string | null => keys.find(k => {
    const ids = named?.[k];
    if (ids) return distinctUuids(read.entries(def, k)).some(id => ids.has(upper(id)));
    return !phpEmpty(read.value(def, k)) || read.entries(def, k).length > 0;
  }) ?? null;
  const own = authors(row);
  if (own) return own;
  for (const carrier of TRIGGER_CARRIERS) {
    for (const raw of carrierEntries(read, row, carrier)) {
      const entry = normaliseEntry(read, raw);
      if (!entry) continue;
      const hit = authors(resolveDefinition(read, entry.item_arr, `carrier:${carrier}`));
      if (hit) return hit;
    }
  }
  for (const status of payloadStatuses(read, row)) {
    const def = record(status.status_arr);
    if (!def) throw new TriggerInputUnavailable('status-definition-not-held');
    const hit = authors(def);
    if (hit) return hit;
  }
  return null;
}
/** Both pools against ONE caster state — a decision PREVIEW (e.g. "could anything fire?"). A real dispatch uses runTriggerDispatch,
 *  which collects the status pool after the carrier dispatches. */
export function collectTriggerCandidates(read: TriggerReaders, casterIn: Row, event: TriggerEvent): { caster: Row; carriers: TriggerCandidate[]; statuses: TriggerCandidate[] } {
  const { caster, carriers } = collectCarrierCandidates(read, casterIn, event);
  return { caster, carriers, statuses: collectStatusCandidates(read, caster, event) };
}

// ---- PASS 2 --------------------------------------------------------------------------------------------------------------------------
function entryHasCharges(read: TriggerReaders, entry: Row): boolean {
  const charges = record(entry.charges);
  if (charges && 'current' in charges) return charges.current !== 'NA';
  if ('ability_charges' in entry) return entry.ability_charges !== 'NA';
  return layerHas(read, record(entry.item_arr) ?? entry, 'ability_charges');
}
/** triggered_ability_entry_charges_available(). */
function chargesAvailable(read: TriggerReaders, entry: Row, abilityId: string, caster: Row): boolean {
  if (chargeReductionDelta(caster, -1, read) === 0) return true;
  if (!entryHasCharges(read, entry)) return true;
  const c = read.currentCharges(entry, abilityId, caster);
  if (c === 'NA' || c === '' || c === null || c === undefined) return true;
  return Math.trunc(Number(c)) > 0;
}
/** triggered_reaction_ability_can_fire(). Exported for build_caster_roll_type_modifiers' reaction walk (ability/skill-roll.ts, lane A8). */
export function reactionCanFire(read: TriggerReaders, entry: Row, abilityId: string, caster: Row): boolean {
  if (!chargesAvailable(read, entry, abilityId, caster)) return false;
  return !(read.inCombat(caster) && read.scalar(caster, 'reaction') <= 0);
}
/** triggered_spell_can_fire(). */
function spellCanFire(read: TriggerReaders, entry: Row, abilityId: string, caster: Row, carrier: TriggerCarrier): boolean {
  const arr = record(entry.item_arr);
  if (!arr) return false;
  if ((read.value(arr, 'spell_casting_time') ?? '') !== 'reaction') return false;
  if (read.inCombat(caster) && read.scalar(caster, 'reaction') <= 0) return false;
  if (carrier === 'innate_spell_per_long_rest') { if (!chargesAvailable(read, entry, abilityId, caster)) return false; }
  else if (carrier === 'spells_known') {
    const level = Math.trunc(Number(read.value(arr, 'spell_level') ?? 0)) || 0;
    if (level > 0 && read.scalar(caster, 'mana') < -manaReductionDelta(caster, -level, read)) return false;
  }
  const delta = read.value(arr, 'change_caster_mana');
  if (delta !== null && delta !== undefined) {
    const d = Math.trunc(Number(delta)) || 0;
    if (d < 0 && read.scalar(caster, 'mana') < -manaReductionDelta(caster, d, read)) return false;
  }
  return chargesAvailable(read, entry, abilityId, caster);
}
/** passive_ability_entry_is_active(): truthiness of the compile stamp; absent = active. */
export function passiveActive(read: Pick<TriggerReaders, 'value'>, entry: Row): boolean {
  if (!('passive_conditions_active' in entry)) return true;
  return !phpEmpty(read.value(entry, 'passive_conditions_active'));
}
/** passive_ability_trigger_conditions_met(). */
export function passiveConditionsMet(read: TriggerReaders, passive: Row, target: Row | null): boolean {
  // v_status_id_of() compares the NEEDED id raw; has_status_effect() resolves a quantity alias first (Codex S1 F3).
  const holds = (t: Row, id: string): boolean => VITALITY_STATUS_IDS.has(id) && read.vStatusId(t) === id
    || read.statusItemIds(t).includes(resolveQuantityAlias(id));
  if (layerHas(read, passive, 'if_target_has_status_effect_active')) {
    if (!target) return false;
    let found = false;
    for (const e of read.entries(passive, 'if_target_has_status_effect_active')) { const id = layerListEntryUuid(e); if (id && holds(target, id)) { found = true; break; } }
    if (!found) return false;
  }
  if (layerHas(read, passive, 'if_target_has_status_effect_not_active') && target) {
    for (const e of read.entries(passive, 'if_target_has_status_effect_not_active')) { const id = layerListEntryUuid(e); if (id && holds(target, id)) return false; }
  }
  return true;
}
/** janus_trigger_entry_summon_sub_type_allows(): a triggers_on_caster_summon_death carrier scoped by
 *  triggers_on_caster_summon_death_creature_sub_type fires only when the DYING SUMMON (the inciting row) carries one of those
 *  sub-types (any division, its full compiled creature_sub_type set). Other triggers / no scope pass; unknown summon fails closed. */
export function summonSubTypeAllows(read: TriggerReaders, trigger: string, ability: Row, inciting: Row | null): boolean {
  if (trigger !== 'triggers_on_caster_summon_death') return true;
  const wanted = distinctUuids(read.entries(ability, 'triggers_on_caster_summon_death_creature_sub_type'));
  if (wanted.length === 0) return true;
  if (!inciting) return false;
  const have = distinctUuids(read.entries(inciting, 'creature_sub_type'));
  return have.some(id => wanted.includes(id));
}
/** decode_trigger_target_ids_list(). */
function decodeTargetIds(raw: string): string[] {
  let decoded: unknown;
  try { decoded = JSON.parse(raw); } catch { return []; }
  if (!Array.isArray(decoded) && !record(decoded)) return [];
  const out: string[] = [];
  for (const v of Object.values(decoded as Row)) if (typeof v === 'string' && v !== '' && isUuid(v) && !out.includes(v)) out.push(v);
  return out;
}
/** janus_trigger_passes_db_gate(): the caster's compiled mmdb.triggers rows. */
export function passesDbGate(read: Pick<TriggerReaders, 'value'>, abilityId: string, caster: Row, incitingId: string | null): boolean {
  const rows = caster.triggers;
  if (!Array.isArray(rows) || rows.length === 0) return true;
  for (const raw of rows) {
    const row = record(raw); if (!row) continue;
    const rowAbility = String(read.value(row, 'trigger_ability_id') ?? '');
    if (rowAbility === '' || rowAbility !== abilityId) continue;
    if (Math.trunc(Number(read.value(row, 'trigger_inactive') ?? 0)) === 1) return false;
    const targets = read.value(row, 'trigger_target_ids') ?? '';
    if (typeof targets === 'string' && targets !== '') {
      const ids = decodeTargetIds(targets);
      if (ids.length && (!incitingId || !ids.includes(incitingId))) return false;
    }
  }
  return true;
}
/** janus_trigger_entry_in_range(). */
function inRangeGate(read: TriggerReaders, ability: Row, caster: Row, inciting: Row | null): boolean {
  if (!inciting) return true;
  if (read.itemField(ability, 'item_type') === 'opportunity_attack_ability' && phpEmpty(read.value(ability, 'range'))) {
    const d = read.distanceFt(caster, inciting);
    if (d === null) return true;
    const reach = Number(read.value(caster, 'reach') ?? 5);
    return d <= reach;
  }
  if (phpEmpty(read.value(ability, 'range'))) return true;
  if (!phpEmpty(read.value(ability, 'aoe_type'))) return true;
  return read.inRange(caster, inciting, ability);
}
/** janus_trigger_entry_passes_in_combat_requirement(). */
function inCombatGate(read: TriggerReaders, ability: Row, caster: Row): boolean {
  const oa = read.itemField(ability, 'item_type') === 'opportunity_attack_ability';
  if (!(layerHas(read, ability, 'requires_in_combat') || oa)) return true;
  return read.inCombat(caster);
}
/** janus_trigger_entry_team_type_allows_inciting(). */
function teamGate(read: TriggerReaders, ability: Row, caster: Row, inciting: Row | null): boolean {
  if (!inciting) return true;
  if (!phpEmpty(read.value(ability, 'aoe_type'))) return true;
  const oa = read.itemField(ability, 'item_type') === 'opportunity_attack_ability';
  const opposingOnly = !phpEmpty(read.value(ability, 'effect_only_affects_opposing_team_type')) || oa;
  const sameOnly = !phpEmpty(read.value(ability, 'effect_only_affects_same_team_type'));
  if (!opposingOnly && !sameOnly) return true;
  const opposing = read.opposing(caster, inciting);
  if (opposingOnly && !opposing) return false;
  if (sameOnly && opposing) return false;
  return true;
}
/** janus_trigger_entry_weapon_resolves(). */
export function weaponGate(read: TriggerReaders, ability: Row, caster: Row): boolean {
  const type = read.itemField(ability, 'item_type');
  if (type !== 'attack_ability' && type !== 'opportunity_attack_ability') return true;
  if (!phpEmpty(read.value(ability, 'draw_sheathed_weapon_for_attack_ability'))) return false;
  // PHP loose `== 0`: null / '' / 0 / '0' all compare equal to 0.
  // PHP 8 `$v == 0`: null / false / 0 / a NUMERIC string equal to 0 → true; '' and any non-numeric string → false (Codex S1 F8).
  const zero = (v: unknown): boolean => {
    if (v === null || v === undefined || v === false) return true;
    if (typeof v === 'number') return v === 0;
    if (typeof v === 'string') return /^\s*[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?\s*$/.test(v) && Number(v.trim()) === 0;
    return false;
  };
  const none = zero(read.value(caster, 'count_main_hand_weapon_equipped')) && zero(read.value(caster, 'count_off_hand_weapon_equipped'))
    && zero(read.value(caster, 'count_both_hands_weapon_equipped')) && read.naturalWeaponCount(caster) === 0;
  return !none;
}
/** AoeType::LIST (additions.php) — id → variable_name. */
const AOE_TYPE_NAMES: Record<string, string> = {
  'C53BE2B6-07EA-4B3B-9664-1AD286C44AF9': 'circle', '0FEF0148-6B62-4601-A8A1-A5A1A22905D8': 'cone', '0DBFA54E-F921-4835-B159-2A4AEFA62408': 'cube',
  'E8B770D7-1BC3-4035-A29C-B99047884CC1': 'cylinder', '56D1C490-B589-4B17-889A-59D866E38D54': 'line', '981032B8-4F0B-43C9-8D98-67C693E4DB42': 'sphere',
  '3F14BF23-09A6-4C05-AFD9-EE0CCD2596A3': 'square',
};
/** PHP's float → string (`precision` = 14, %.14G): integral values print bare, others at 14 significant digits, E-notation outside
 *  [1e-4, 1e15). The AoE span is a string the commit re-parses, so the browser must write PHP's exact text. */
export function phpFloatString(n: number): string {
  if (!Number.isFinite(n)) return n > 0 ? 'INF' : n < 0 ? '-INF' : 'NAN';
  if (n === 0) return Object.is(n, -0) ? '-0' : '0';
  const exp = Math.floor(Math.log10(Math.abs(Number(n.toPrecision(14)))));
  if (exp < -4 || exp >= 14) { // %.14G switches at the precision (Codex S1 F9)
    const [mant, e] = n.toExponential(13).split('e');
    const m = mant!.includes('.') ? mant!.replace(/0+$/, '').replace(/\.$/, '.0') : mant + '.0';
    const ev = Number(e);
    return `${m}E${ev < 0 ? '-' : '+'}${Math.abs(ev)}`;
  }
  const fixed = n.toPrecision(14);
  return fixed.includes('.') ? fixed.replace(/0+$/, '').replace(/\.$/, '') : fixed;
}
/** janus_trigger_auto_place_aoe(): the owner-anchored six-coordinate span "encounter_id|ox:oy:oz:dx:dy:dz". */
export function autoPlaceAoe(read: TriggerReaders, owner: Row, anchor: Row, ability: Row): string {
  const encounterId = read.encounterId;
  if (!isUuid(encounterId)) return '';
  const ox = read.position(owner, 'x_pos') ?? 0, oy = read.position(owner, 'y_pos') ?? 0, oz = read.position(owner, 'z_pos') ?? 0;
  const tx = read.position(anchor, 'x_pos') ?? ox, ty = read.position(anchor, 'y_pos') ?? oy;
  const distance = Number(read.value(ability, 'aoe_distance') ?? 0) || 0;
  const typeId = String(read.value(ability, 'aoe_type') ?? '');
  // AoeType::getVariableName() matches the id CASE-SENSITIVELY: a lowercase id resolves to nothing (Codex S1 F7).
  const name = UUID.test(typeId) ? (AOE_TYPE_NAMES[typeId] ?? '') : typeId.toLowerCase();
  let dx: number, dy: number;
  if (['sphere', 'circle', 'cube', 'square', 'cylinder'].includes(name)) { dx = ox; dy = oy; }
  else {
    const vx = tx - ox, vy = ty - oy, len = Math.sqrt(vx * vx + vy * vy);
    if (len < 0.01 || distance <= 0) { dx = ox + distance; dy = oy; }
    else { dx = ox + (vx / len) * distance; dy = oy + (vy / len) * distance; }
  }
  return `${encounterId}|${[ox, oy, oz, dx, dy, oz].map(phpFloatString).join(':')}`;
}

/** janus_trigger_live_carrier_entry(): the entry's LIVE wrapper with the resolved definition grafted back on. */
function liveCarrierEntry(read: TriggerReaders, caster: Row, candidate: TriggerCandidate): Row | null {
  for (const raw of carrierEntries(read, caster, candidate.carrier!)) {
    const live = normaliseEntry(read, raw);
    if (!live) continue;
    if (abilityEntryId(read, live) !== candidate.abilityId) continue;
    return { ...live, item_arr: candidate.ability };
  }
  return null;
}

/** pick_oa_kill_replacement_target(): the CLOSEST living (not dead, v_current > 0) opposing creature still inside the OA's own reach
 *  gate, never the dying target itself; the first of equal distances wins (strict <). null = the dispatch silently skips. */
export function pickOaKillReplacement(read: TriggerReaders, roster: Row[], caster: Row, excluded: Row | null, ability: Row): Row | null {
  const idOf = (row: Row | null): string => { const v = record(row?.encounter_creatures)?.encounter_creature_id; return typeof v === 'string' ? v : ''; };
  const excludedId = idOf(excluded);
  let best: Row | null = null, bestDistance = Number.MAX_SAFE_INTEGER;
  for (const candidate of roster) {
    const cid = idOf(candidate);
    if (cid === '' || cid === excludedId) continue;
    let dead: unknown = record(candidate.encounter_creatures)?.encounter_creature_dead ?? 0;
    if (Array.isArray(dead)) dead = dead[0];
    if (Math.trunc(Number(dead)) === 1) continue;
    let v: unknown = record(record(candidate.computed)?.vitality)?.v_current ?? 0;
    if (Array.isArray(v)) v = v[0];
    if (Math.trunc(Number(v) || 0) <= 0) continue;
    if (!read.opposing(caster, candidate)) continue;
    if (!inRangeGate(read, ability, caster, candidate)) continue;
    const distance = read.distanceFt(caster, candidate);
    if (distance === null) return unavailable('oa-kill-replacement:distance-unmeasurable');
    if (distance < bestDistance) { best = candidate; bestDistance = distance; }
  }
  return best;
}
function oaKillReplacementFor(read: TriggerReaders, caster: Row, excluded: Row | null, ability: Row): Row | null {
  if (read.oaKillReplacement) return read.oaKillReplacement(caster, excluded, ability);
  if (read.roster) return pickOaKillReplacement(read, read.roster(), caster, excluded, ability);
  return unavailable('oa-kill-replacement-reader');
}

/** PASS 2 for ONE candidate against the caster's CURRENT state. */
export function gateTriggerCandidate(read: TriggerReaders, candidate: TriggerCandidate, caster: Row, event: TriggerEvent, inciting: Row | null): TriggerDecision {
  // janus_trigger() keeps the inciting row's id AS STORED (its trigger_target_ids gate is a strict in_array — Codex S1 F6).
  const rawIncitingId = inciting ? record(inciting.encounter_creatures)?.encounter_creature_id : null;
  const ability = candidate.ability, id = candidate.abilityId, incitingId = typeof rawIncitingId === 'string' && rawIncitingId !== '' ? rawIncitingId : null;
  const noAoe = phpEmpty(read.value(ability, 'aoe_type'));
  if (candidate.pool === 'carrier') {
    const carrier = candidate.carrier!;
    const entry = liveCarrierEntry(read, caster, candidate);
    if (!entry) return { fire: false, gate: 'live-entry-gone' };
    if (!summonSubTypeAllows(read, event.trigger, ability, inciting)) return { fire: false, gate: 'summon-sub-type' };
    // janus_trigger_self_source_allows() runs BEFORE the carrier gates (janus_trigger.php ~L371; Round 30 P3, AB3): a self-sourced event
    // must never reach spellCanFire's reads / hand-backs.
    if (Number(read.value(ability, 'trigger_ignores_self_source')) === 1 && inciting && ecId(inciting) === ecId(caster)) return { fire: false, gate: 'self-source' };
    if (carrier === 'passive_ability') {
      if (!passiveActive(read, entry)) return { fire: false, gate: 'passive-inactive' };
      if (!passiveConditionsMet(read, ability, inciting)) return { fire: false, gate: 'passive-conditions' };
      if (!chargesAvailable(read, entry, id, caster)) return { fire: false, gate: 'charges' };
    } else if (carrier === 'reaction_ability' || carrier === 'opportunity_attack_ability') {
      if (!reactionCanFire(read, entry, id, caster)) return { fire: false, gate: 'reaction-can-fire' };
    } else if (carrier === 'free_ability' || carrier === 'menta' || carrier === 'curse') {
      if (!chargesAvailable(read, entry, id, caster)) return { fire: false, gate: 'charges' };
    } else if (!spellCanFire(read, entry, id, caster, carrier)) return { fire: false, gate: 'spell-can-fire' };
    if (read.inCombat(caster) && candidate.competes && read.scalar(caster, 'reaction') <= 0) return { fire: false, gate: 'reaction-budget' };
    if (!passesDbGate(read, id, caster, incitingId)) return { fire: false, gate: 'db-opt-out' };
    if (!inRangeGate(read, ability, caster, inciting)) return { fire: false, gate: 'range' };
    if (!inCombatGate(read, ability, caster)) return { fire: false, gate: 'in-combat' };
    if (!teamGate(read, ability, caster, inciting)) return { fire: false, gate: 'team' };
    if (event.trigger === ZERO_VITALITY_WITNESS && !zeroVitalityWitnessAllows(read, ability, caster, event.zeroVitalityVictim ?? null))
      return { fire: false, gate: 'zero-vitality-witness' };
    if (!weaponGate(read, ability, caster)) return { fire: false, gate: 'weapon' };
    if (read.conditionsMet && !read.conditionsMet(ability, caster)) return { fire: false, gate: 'conditions' };
    let targets: TriggerDispatchSpec['targets'] = ['frame'];
    const aoe = noAoe ? '' : autoPlaceAoe(read, caster, inciting ?? caster, ability);
    if (carrier !== 'passive_ability' && noAoe && inciting) targets = [incitingId ?? ''];
    if (carrier === 'opportunity_attack_ability' && event.trigger === 'triggers_on_kill' && noAoe) {
      const replacement = oaKillReplacementFor(read, caster, inciting, ability);
      if (!replacement) return { fire: false, gate: 'oa-kill-no-replacement' };
      targets = [ecId(replacement)];
    }
    if (carrier === 'passive_ability' && noAoe) targets = ['caster'];
    return { fire: true, spec: { candidate, targets, aoe,
      variable1: carrier === 'at_will_spell' ? 'at_will_spell' : carrier === 'innate_spell_per_long_rest' ? 'innate_spell_per_long_rest' : null,
      incitingForPassive: carrier === 'passive_ability' && noAoe && inciting ? incitingId : null } };
  }
  // status pool — its own gate order (PHP runs the reaction budget LAST here)
  if (Number(read.value(ability, 'trigger_ignores_self_source')) === 1 && inciting && ecId(inciting) === ecId(caster)) return { fire: false, gate: 'self-source' };
  if (!summonSubTypeAllows(read, event.trigger, ability, inciting)) return { fire: false, gate: 'summon-sub-type' };
  if (!passesDbGate(read, id, caster, incitingId)) return { fire: false, gate: 'db-opt-out' };
  if (!inRangeGate(read, ability, caster, inciting)) return { fire: false, gate: 'range' };
  if (!inCombatGate(read, ability, caster)) return { fire: false, gate: 'in-combat' };
  if (!teamGate(read, ability, caster, inciting)) return { fire: false, gate: 'team' };
  if (event.trigger === ZERO_VITALITY_WITNESS && !zeroVitalityWitnessAllows(read, ability, caster, event.zeroVitalityVictim ?? null))
    return { fire: false, gate: 'zero-vitality-witness' };
  if (!weaponGate(read, ability, caster)) return { fire: false, gate: 'weapon' };
  if (read.conditionsMet && !read.conditionsMet(ability, caster)) return { fire: false, gate: 'conditions' };
  // A charged ability's lingering row uses the same live pool as its bucket carrier.
  // A bucket fire can exhaust it before this status is visited in the same family.
  if (layerHas(read, ability, 'ability_charges')) {
    let pool: Row | null = null;
    for (const bucket of ABILITY_CHARGE_BUCKETS) {
      const entry = read.entries(caster, bucket).find(raw => chargeEntryId(raw) === upper(id));
      if (entry) { pool = { ...record(entry), item_arr: ability }; break; }
    }
    if (!pool && !event.statusIsCharge) return unavailable(`charged-status-pool-not-held:${id}`);
    if (pool && !chargesAvailable(read, pool, id, caster)) return { fire: false, gate: 'charges' };
  }
  if (read.inCombat(caster) && candidate.competes && read.scalar(caster, 'reaction') <= 0) return { fire: false, gate: 'reaction-budget' };
  const passive = read.itemField(ability, 'item_type') === 'passive_ability';
  let targets: TriggerDispatchSpec['targets'] = ['frame'];
  const aoe = noAoe ? '' : autoPlaceAoe(read, caster, inciting ?? caster, ability);
  if (passive && noAoe) targets = ['caster'];
  else if (noAoe && inciting) targets = [incitingId ?? ''];
  if (read.itemField(ability, 'item_type') === 'opportunity_attack_ability' && event.trigger === 'triggers_on_kill' && noAoe) {
    const replacement = oaKillReplacementFor(read, caster, inciting, ability);
    if (!replacement) return { fire: false, gate: 'oa-kill-no-replacement' };
    targets = [ecId(replacement)];
  }
  return { fire: true, spec: { candidate, targets, aoe, variable1: null, incitingForPassive: passive && noAoe && inciting ? incitingId : null } };
}

/** One dispatch the host performs. It returns the caster row as it stands AFTER the effect (its reaction spent, charges written …). */
export type TriggerExecutor = (spec: TriggerDispatchSpec, caster: Row) => Row;
export interface TriggerDispatchResult { caster: Row; dispatched: TriggerDispatchSpec[]; decisions: Array<{ candidate: TriggerCandidate; decision: TriggerDecision }> }

/** janus_trigger(): pass 1, then gate → execute per candidate (carrier pool, then status pool). Returns the dispatch count's specs. */
export function runTriggerDispatch(read: TriggerReaders, casterIn: Row, event: TriggerEvent, inciting: Row | null, execute: TriggerExecutor,
  attempt?: (candidate: TriggerCandidate, run: () => void) => boolean): TriggerDispatchResult {
  const { caster: injected, carriers } = collectCarrierCandidates(read, casterIn, event);
  let caster = injected;
  const dispatched: TriggerDispatchSpec[] = [];
  const decisions: TriggerDispatchResult['decisions'] = [];
  // Multiple grants can name the same reaction. OOC has no reaction debit to
  // close its gate, so settle each identity once within this triggering event.
  const reactionsFired = new Set<string>();
  const run = (candidate: TriggerCandidate): void => {
    const reactionId = candidate.competes ? upper(candidate.abilityId) : null;
    let spec: TriggerDispatchSpec | null = null;
    const dispatch = (): void => {
      if (reactionId && reactionsFired.has(reactionId)) {
        decisions.push({ candidate, decision: { fire: false, gate: 'already-dispatched' } });
        return;
      }
      const decision = gateTriggerCandidate(read, candidate, caster, event, inciting);
      decisions.push({ candidate, decision });
      if (!decision.fire) return;
      caster = execute(decision.spec, caster);
      spec = decision.spec;
      if (reactionId) reactionsFired.add(reactionId);
    };
    const completed = attempt ? attempt(candidate, dispatch) : (dispatch(), true);
    if (!completed && spec && reactionId) reactionsFired.delete(reactionId);
    if (completed && spec) dispatched.push(spec);
  };
  for (const candidate of carriers) run(candidate);
  for (const candidate of collectStatusCandidates(read, caster, event)) run(candidate);
  return { caster, dispatched, decisions };
}
